import { Button } from "@threadws/jute";
import { formatDay, shiftDay, today, type Day } from "../../utils/calendarDay";

/** Whole days from today to `day`, e.g. -1 for yesterday. */
const daysFromToday = (day: Day) => Math.round((Date.parse(day) - Date.parse(today())) / 86_400_000);

/** "Today", "Tomorrow", "In 3 days", "2 days ago"… */
function relativeLabel(day: Day): string {
  const offset = daysFromToday(day);
  if (offset === 0) return "Today";
  if (offset === 1) return "Tomorrow";
  if (offset === -1) return "Yesterday";
  return offset > 0 ? `In ${offset} days` : `${-offset} days ago`;
}

interface DayHeadingProps {
  day: Day;
  onChange: (day: Day) => void;
}

/** The day being shown, with buttons to step a day either way or jump back to today. */
export function DayHeading({ day, onChange }: DayHeadingProps) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div className="flex flex-col gap-1.5">
        <span className="font-babyblue text-pixel-1 text-accent-fg">{relativeLabel(day)}</span>
        <h2 className="font-display text-3xl leading-tight font-light">{formatDay(day)}</h2>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="secondary" icon="" aria-label="Previous day" onClick={() => onChange(shiftDay(day, -1))}>
          ‹
        </Button>
        <Button variant="ghost" icon="" disabled={day === today()} onClick={() => onChange(today())}>
          Today
        </Button>
        <Button variant="secondary" icon="" aria-label="Next day" onClick={() => onChange(shiftDay(day, 1))}>
          ›
        </Button>
      </div>
    </div>
  );
}
